import React from 'react';
import { cn } from '../../utils/cn';

export interface SegmentedToggleItem<T extends string> {
  key: T;
  label: string;
  icon?: React.ReactNode;
}

interface Props<T extends string> {
  items: SegmentedToggleItem<T>[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel?: string;
  fullWidth?: boolean;
  minTabWidth?: string;
  testIdPrefix?: string;
}

export function SegmentedToggle<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
  fullWidth = false,
  minTabWidth = '100px',
  testIdPrefix,
}: Props<T>) {
  const activeIndex = Math.max(
    0,
    items.findIndex((i) => i.key === value)
  );
  const n = items.length;
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex rounded-lg bg-gray-100 p-1 ring-1 ring-inset ring-gray-200/80',
        fullWidth ? 'w-full' : 'w-full sm:w-auto'
      )}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-1 left-1 rounded-md bg-primary-500 shadow-sm ring-1 ring-inset ring-primary-600/20 transition-transform duration-200 ease-out"
        style={{
          width: `calc(${100 / n}% - ${0.5 / n}rem)`,
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={value === item.key}
          onClick={() => onChange(item.key)}
          style={{ minWidth: minTabWidth }}
          data-test-id={testIdPrefix ? `${testIdPrefix}-${item.key}` : undefined}
          className={cn(
            'relative z-10 flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors whitespace-nowrap',
            value === item.key ? 'text-white' : 'text-gray-500 hover:text-gray-800'
          )}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}
